// ============================================
// APP: NVA (React App)
// FILE: src/contexts/EventsContext.tsx
// PURPOSE: Centralized event fetching with in-memory cache
//   Gli eventi arrivano da /api/events (api/events.ts): Mola e 14 paesi
//   intorno, letti sul server con il raggio 0 per paese, 30 giorni, con la
//   distanza da Mola. Da lì un solo fetch serve Esplora (7 giorni, 12),
//   le pagine città (8) e la pagina Eventi (tutti). Se la funzione non
//   c'è (sviluppo locale) o non risponde, si ripiega sul vecchio metodo
//   via /api/proxy. La lettura dell'HTML sta in lib/eventsParser.
// ============================================

import React, { createContext, useContext, useCallback, useRef } from 'react';
import { tr } from '../i18n';
import {
  dayKey,
  KNOWN_CITIES,
  makeEventId,
  makeEventKey,
  parseEventsFromHtml,
} from '../lib/eventsParser';
import { romeDayKey, shiftDayKey } from '../lib/romeDay';

export interface Event {
  id: string;
  title: string;
  startDate: Date;
  endDate?: Date;
  city: string;
  description?: string;
  link?: string;
  imageUrl?: string;
  isNew?: boolean;
  /** Km interi da Mola del paese dell'evento (0 per Mola); li mette /api/events. */
  distanceKm?: number;
}

interface CacheEntry {
  events: Event[];
  fetchedAt: number;
  /** '__all__': la lista viene dalla rete (non solo da localStorage). */
  fromNetwork?: boolean;
}

interface EventsContextValue {
  /**
   * Gli eventi di una sorgente, da /api/events (un solo fetch per tutte).
   * @param source - 'explore': i prossimi 7 giorni di tutti i paesi, al massimo 12;
   *   oppure lo slug di una città (es. 'bari', 'monopoli'): i suoi eventi, al massimo 8.
   */
  getEvents: (source: string) => Promise<Event[]>;
  /**
   * TUTTI gli eventi dei prossimi 30 giorni (Mola e paesi intorno), senza doppioni e in ordine di data.
   * Di norma torna subito quelli salvati (localStorage) e aggiorna in background;
   * con `{ fresh: true }` aspetta la lista della rete, con i "Nuovo" rispetto a quelli salvati.
   */
  getAllEvents: (options?: { fresh?: boolean }) => Promise<Event[]>;
  enrichTimes: (events: Event[]) => Promise<Event[]>;
  invalidate: (source?: string) => void;
}

const CACHE_TTL = 10 * 60 * 1000; // 10 minutes

/** Esplora: gli eventi dei prossimi 7 giorni (oggi compreso), al massimo 12. */
const EXPLORE_DAYS = 7;
const EXPLORE_MAX = 12;
/** Pagine città: al massimo 8 eventi della città. */
const CITY_MAX = 8;
/** Pagina Eventi: i prossimi 30 giorni. */
const ALL_DAYS = 30;

/** La funzione serverless con gli eventi di tutti i paesi (cache CDN di 2 ore). */
const EVENTS_API = '/api/events';
/** Se la CDN non ha ancora la risposta, il server legge 15 paesi: qualche secondo. */
const EVENTS_API_TIMEOUT_MS = 15000;
/** Dopo un errore di /api/events, per 2 minuti si va dritti al proxy (niente attese ripetute). */
const EVENTS_API_RETRY_MS = 2 * 60 * 1000;
/** Chiave della cache in memoria per la risposta intera di /api/events. */
const API_CACHE_KEY = '__api__';

// Map source key to URL slug. Serve solo al ripiego via proxy (sviluppo
// locale senza funzioni, o /api/events giù): il raggio passa per il cookie
// globale di api/proxy.ts e può arrivare sbagliato.
const SOURCE_SLUGS: Record<string, string> = {
  explore: 'mola-di-bari',
  bari: 'bari',
  'mola-di-bari': 'mola-di-bari',
  monopoli: 'monopoli',
  'polignano-a-mare': 'polignano-a-mare',
  alberobello: 'alberobello',
  locorotondo: 'locorotondo',
  trani: 'trani',
  'castellana-grotte': 'castellana-grotte',
  matera: 'matera',
  lecce: 'lecce',
  ostuni: 'ostuni',
  zoosafari: 'fasano',
};

/**
 * Città con una pagina nell'app ma fuori dai paesi dell'API (Trani è a più di
 * 40 km): senza questo elenco il filtro per città non scatterebbe e la
 * pagina mostrerebbe gli eventi degli altri paesi. Qui di eventi non ce ne
 * sono (stato vuoto con "Vedi tutti"), mai quelli sbagliati.
 */
const EXTRA_CITIES = ['Trani', 'Matera', 'Lecce', 'Ostuni'];

/** Pagine che non sono un paese: lo Zoosafari mostra gli eventi di Fasano. */
const SOURCE_CITY_ALIASES: Record<string, string> = { zoosafari: 'Fasano' };

/** Il nome della città di uno slug ('mola-di-bari' → 'Mola di Bari'), come nei nomi del sito. */
function cityOfSource(source: string): string | undefined {
  if (SOURCE_CITY_ALIASES[source]) return SOURCE_CITY_ALIASES[source];
  return KNOWN_CITIES.concat(EXTRA_CITIES).find(c => c.toLowerCase().replace(/ /g, '-') === source);
}

// ─── Finestre di giorni ───
// I giorni sono quelli di Roma, come le chip di Esplora (romeToday); il
// giorno di un evento si legge dai campi locali della data (mezzanotte del
// telefono, vedi dateFromDay).

/** Solo gli eventi in corso o che iniziano entro `days` giorni da oggi (oggi compreso). */
function withinDays(events: Event[], days: number, today: string = romeDayKey(new Date())): Event[] {
  const last = shiftDayKey(today, days - 1);
  return events.filter((e) => {
    const start = dayKey(e.startDate);
    const end = e.endDate ? dayKey(e.endDate) : start;
    return end >= today && start <= last;
  });
}

/**
 * Esplora: al massimo 12 eventi dei prossimi 7 giorni. Presi a giro, uno per
 * giorno alla volta (così oggi e domani non si mangiano il weekend) e in
 * ogni giorno prima un paese non ancora preso, poi il più vicino a Mola
 * (così non è tutta Bari, che da sola ha più di metà degli eventi).
 * Gli eventi già cominciati prima di oggi (mostre) solo in coda, se resta posto.
 */
function pickExploreEvents(events: Event[], today: string = romeDayKey(new Date())): Event[] {
  const inWindow = withinDays(events, EXPLORE_DAYS, today);
  const byDay = new Map<string, Event[]>();
  const ongoing: Event[] = [];
  for (const e of inWindow) {
    const day = dayKey(e.startDate);
    if (day < today) { ongoing.push(e); continue; }
    const list = byDay.get(day) || [];
    list.push(e);
    byDay.set(day, list);
  }

  const distance = (e: Event) => (e.distanceKm === undefined ? Number.MAX_SAFE_INTEGER : e.distanceKm);
  const days = Array.from(byDay.keys()).sort();
  days.forEach((d) => byDay.get(d)!.sort((a, b) => distance(a) - distance(b)));

  const picked: Event[] = [];
  const usedCities = new Set<string>();
  let progress = true;
  while (picked.length < EXPLORE_MAX && progress) {
    progress = false;
    for (const d of days) {
      if (picked.length >= EXPLORE_MAX) break;
      const list = byDay.get(d)!;
      if (list.length === 0) continue;
      let index = list.findIndex((e) => !usedCities.has(e.city));
      if (index < 0) index = 0;
      const [event] = list.splice(index, 1);
      picked.push(event);
      usedCities.add(event.city);
      progress = true;
    }
  }

  const byDate = (a: Event, b: Event) => a.startDate.getTime() - b.startDate.getTime() || distance(a) - distance(b);
  picked.sort(byDate);
  ongoing.sort(byDate);
  return picked.concat(ongoing.slice(0, Math.max(0, EXPLORE_MAX - picked.length)));
}

/**
 * Gli eventi di una sorgente: Esplora (7 giorni, 12) o una città (i suoi, 8).
 * Per la città, come in Esplora, prima quelli che iniziano da oggi in poi e
 * le mostre già cominciate solo in coda: altrimenti a Bari 4 posti su 8
 * andavano a mostre aperte da settimane.
 */
function selectForSource(source: string, events: Event[]): Event[] {
  if (source === 'explore') return pickExploreEvents(events);
  const city = cityOfSource(source);
  const today = romeDayKey(new Date());
  const mine = withinDays(events, ALL_DAYS, today).filter((e) => !city || e.city === city);
  const upcoming = mine.filter((e) => dayKey(e.startDate) >= today);
  const ongoing = mine.filter((e) => dayKey(e.startDate) < today);
  return upcoming.concat(ongoing).slice(0, CITY_MAX);
}

// ─── /api/events ───

/** "2026-09-25" (o un ISO completo) → mezzanotte del telefono di quel giorno, come le date lette dal sito. */
function dateFromDay(value: unknown): Date | undefined {
  if (typeof value !== 'string') return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return undefined;
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(date.getTime()) ? undefined : date;
}

function eventFromApi(raw: any): Event | null {
  if (!raw || typeof raw.title !== 'string' || !raw.title.trim()) return null;
  const startDate = dateFromDay(raw.startDate);
  if (!startDate) return null;
  const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
  return {
    // Lo stesso id deterministico di sempre, ricalcolato qui: preferiti e promemoria restano collegati.
    id: makeEventId(raw.title, startDate),
    title: raw.title,
    startDate,
    endDate: dateFromDay(raw.endDate),
    city: str(raw.city) || tr('events.unknownCity'),
    description: str(raw.description),
    link: str(raw.link),
    imageUrl: str(raw.imageUrl),
    distanceKm: typeof raw.distanceKm === 'number' && isFinite(raw.distanceKm) ? raw.distanceKm : undefined,
  };
}

/**
 * Tutti gli eventi (30 giorni, tutti i paesi) da /api/events. Lancia un
 * errore se la funzione non c'è (in sviluppo il dev server risponde con
 * index.html) o non risponde: chi chiama ripiega sul proxy.
 */
async function fetchApiEvents(): Promise<Event[]> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), EVENTS_API_TIMEOUT_MS);
  try {
    const response = await fetch(EVENTS_API, { signal: controller.signal, headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    const data = await response.json();
    if (!data || !Array.isArray(data.events)) throw new Error('Unexpected /api/events response');
    const events: Event[] = [];
    for (const raw of data.events) {
      const event = eventFromApi(raw);
      if (event) events.push(event);
    }
    events.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
    return events;
  } finally {
    clearTimeout(timeout);
  }
}

/** Copie degli eventi: enrichTimes e l'applicazione degli orari li modificano. */
const copyEvents = (events: Event[]): Event[] => events.map((e) => ({ ...e }));

// ─── Event time scraping ───
const TIME_STORAGE_KEY = 'nva-event-times';
const TIME_REGEX = /ore\s+(\d{1,2})[:.:](\d{2})/i;

function loadTimeCache(): Record<string, { hours: number; minutes: number }> {
  try {
    return JSON.parse(localStorage.getItem(TIME_STORAGE_KEY) || '{}');
  } catch { return {}; }
}

function saveTimeCache(cache: Record<string, { hours: number; minutes: number }>): void {
  try { localStorage.setItem(TIME_STORAGE_KEY, JSON.stringify(cache)); } catch {}
}

/**
 * Gli orari già noti (cache `nva-event-times`, riempita dalla pagina Eventi)
 * applicati a una COPIA degli eventi: nessuna richiesta di rete. Esplora lo
 * usa per scrivere "ore 21:00" invece di "tutto il giorno" quando l'ora c'è.
 */
export function withCachedTimes(events: Event[]): Event[] {
  const cache = loadTimeCache();
  return events.map((event) => {
    const known = event.link ? cache[event.link] : undefined;
    if (!known) return event;
    const startDate = new Date(event.startDate);
    startDate.setHours(known.hours, known.minutes, 0, 0);
    return { ...event, startDate };
  });
}

async function fetchEventTime(link: string): Promise<{ hours: number; minutes: number } | null> {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);
    const resp = await fetch(`/api/proxy?url=${encodeURIComponent(link)}`, { signal: controller.signal });
    clearTimeout(timeout);
    if (!resp.ok) return null;
    const html = await resp.text();
    const match = html.match(TIME_REGEX);
    if (match) {
      return { hours: parseInt(match[1]), minutes: parseInt(match[2]) };
    }
    return null;
  } catch { return null; }
}

async function enrichEventsWithTimes(events: Event[]): Promise<Event[]> {
  const timeCache = loadTimeCache();
  const needsFetch: Event[] = [];

  // Apply cached times first
  for (const event of events) {
    if (!event.link) continue;
    const cached = timeCache[event.link];
    if (cached) {
      event.startDate = new Date(event.startDate);
      event.startDate.setHours(cached.hours, cached.minutes, 0, 0);
    } else {
      needsFetch.push(event);
    }
  }

  if (needsFetch.length === 0) return events;

  // Fetch times in batches of 10 concurrently
  const BATCH_SIZE = 10;
  for (let i = 0; i < needsFetch.length; i += BATCH_SIZE) {
    const batch = needsFetch.slice(i, i + BATCH_SIZE);
    const results = await Promise.all(
      batch.map(async (event) => {
        if (!event.link) return null;
        const time = await fetchEventTime(event.link);
        if (time) {
          timeCache[event.link!] = time;
          event.startDate = new Date(event.startDate);
          event.startDate.setHours(time.hours, time.minutes, 0, 0);
        }
        return time;
      })
    );
  }

  saveTimeCache(timeCache);
  return events;
}

// ─── localStorage persistence for events ───
const STORAGE_KEY = 'nva-events-cache';

interface StoredEvent {
  id: string;
  title: string;
  startDate: string; // ISO string
  endDate?: string;
  city: string;
  description?: string;
  link?: string;
  imageUrl?: string;
  distanceKm?: number;
}

interface StoredCache {
  events: StoredEvent[];
  savedAt: number;
}

function saveEventsToStorage(events: Event[]): void {
  try {
    const data: StoredCache = {
      events: events.map(e => ({
        ...e,
        startDate: e.startDate.toISOString(),
        endDate: e.endDate?.toISOString(),
      })),
      savedAt: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch { /* storage full or unavailable */ }
}

function loadEventsFromStorage(): Event[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data: StoredCache = JSON.parse(raw);
    const timeCache = loadTimeCache();

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    // Reconstruct Event objects, filter out past events, apply cached times
    const events: Event[] = [];
    for (const se of data.events) {
      const startDate = new Date(se.startDate);
      const endDate = se.endDate ? new Date(se.endDate) : undefined;
      const effectiveEnd = endDate || startDate;
      if (effectiveEnd < now) continue; // past event, skip
      // Apply cached time if available
      if (se.link && timeCache[se.link]) {
        startDate.setHours(timeCache[se.link].hours, timeCache[se.link].minutes, 0, 0);
      }
      // Regenerate the deterministic id so stored events line up with
      // freshly-scraped ones (and so legacy caches holding random ids from
      // older app versions get normalised on load).
      events.push({ ...se, id: makeEventId(se.title, startDate), startDate, endDate, isNew: false });
    }

    return events.length > 0 ? events : null;
  } catch {
    return null;
  }
}

function diffEvents(oldEvents: Event[], newEvents: Event[]): Event[] {
  const oldKeys = new Set(oldEvents.map(e => makeEventKey(e.title, e.startDate)));
  return newEvents.map(e => ({ ...e, isNew: !oldKeys.has(makeEventKey(e.title, e.startDate)) }));
}

const EventsContext = createContext<EventsContextValue | null>(null);

export const EventsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const cacheRef = useRef<Map<string, CacheEntry>>(new Map());
  const pendingRef = useRef<Map<string, Promise<Event[]>>>(new Map());

  /**
   * La risposta intera di /api/events (30 giorni, tutti i paesi), una sola
   * volta per Esplora, pagine città e pagina Eventi: cache in memoria di 10
   * minuti e un solo fetch alla volta.
   */
  const apiFailedAtRef = useRef(0);
  const getApiEvents = useCallback(async (): Promise<Event[]> => {
    const cached = cacheRef.current.get(API_CACHE_KEY);
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL) {
      return cached.events;
    }
    const pending = pendingRef.current.get(API_CACHE_KEY);
    if (pending) return pending;
    if (Date.now() - apiFailedAtRef.current < EVENTS_API_RETRY_MS) {
      throw new Error('/api/events failed recently');
    }

    const promise = (async () => {
      try {
        const events = await fetchApiEvents();
        cacheRef.current.set(API_CACHE_KEY, { events, fetchedAt: Date.now() });
        return events;
      } catch (error) {
        apiFailedAtRef.current = Date.now();
        throw error;
      } finally {
        pendingRef.current.delete(API_CACHE_KEY);
      }
    })();
    pendingRef.current.set(API_CACHE_KEY, promise);
    return promise;
  }, []);

  /**
   * Ripiego del vecchio metodo: setDintorni + pagina via proxy. Il raggio
   * passa per il cookie globale di api/proxy.ts e può arrivare sbagliato,
   * ma in sviluppo locale (senza funzioni) l'app funziona lo stesso.
   */
  const fetchAllFromSource = useCallback(async (source: string): Promise<Event[]> => {
    const slug = SOURCE_SLUGS[source] || source;
    const radius = source === 'explore' ? 20 : 0;
    const defaultCity = source === 'explore' ? tr('events.unknownCity')
      : cityOfSource(source) || tr('events.unknownCity');

    try {
      await fetch(`/api/proxy?url=${encodeURIComponent(
        `https://iltaccodibacco.it/index.php?md=Gateway&az=setDintorni&val=${radius}`
      )}`);
      await new Promise((r) => setTimeout(r, 500));

      const response = await fetch(`/api/proxy?url=${encodeURIComponent(
        `https://iltaccodibacco.it/${slug}/`
      )}`);
      if (!response.ok) return [];

      const html = await response.text();
      return parseEventsFromHtml(html, defaultCity);
    } catch {
      return [];
    }
  }, []);

  const getEvents = useCallback(async (source: string): Promise<Event[]> => {
    const now = Date.now();
    const cached = cacheRef.current.get(source);

    // Return cached data if fresh
    if (cached && now - cached.fetchedAt < CACHE_TTL) {
      return cached.events;
    }

    // Deduplicate in-flight requests for the same source
    const pending = pendingRef.current.get(source);
    if (pending) return pending;

    const promise = (async () => {
      try {
        let all: Event[];
        try {
          all = await getApiEvents();
        } catch {
          // /api/events non c'è o non risponde: la pagina della sorgente via proxy.
          all = await fetchAllFromSource(source);
        }
        const events = copyEvents(selectForSource(source, all));
        cacheRef.current.set(source, { events, fetchedAt: Date.now() });
        return events;
      } finally {
        pendingRef.current.delete(source);
      }
    })();

    pendingRef.current.set(source, promise);
    return promise;
  }, [getApiEvents, fetchAllFromSource]);

  const getAllEvents = useCallback(async (options?: { fresh?: boolean }): Promise<Event[]> => {
    const now = Date.now();
    // fresh: la lista della rete (quella in arrivo o già arrivata), non quella salvata.
    // Con /api/events la rete risponde prima che la pagina richieda: la vecchia
    // rilettura "invalidate + getAllEvents" ripartiva dallo storage già
    // aggiornato e perdeva i "Nuovo".
    const fresh = !!(options && options.fresh);

    // 1) Check in-memory cache first
    const cached = cacheRef.current.get('__all__');
    if (cached && now - cached.fetchedAt < CACHE_TTL && (!fresh || cached.fromNetwork)) {
      return cached.events;
    }

    // 2) Check localStorage cache — return instantly if available
    const stored = loadEventsFromStorage();
    if (!fresh && stored && stored.length > 0 && !cached) {
      // Set in-memory cache from storage so we don't re-fetch immediately
      cacheRef.current.set('__all__', { events: stored, fetchedAt: now });
    }

    const pendingAll = pendingRef.current.get('__all__');
    if (pendingAll) return pendingAll;

    const promise = (async () => {
      try {
        let freshEvents: Event[];
        try {
          // Tutti i paesi in un colpo, già senza doppioni e in ordine di data.
          freshEvents = copyEvents(withinDays(await getApiEvents(), ALL_DAYS));
        } catch {
          // Ripiego: le vecchie sorgenti via proxy, unite e senza doppioni.
          const sources = Object.keys(SOURCE_SLUGS);
          const results = await Promise.all(sources.map(s => fetchAllFromSource(s)));

          const uniqueMap = new Map<string, Event>();
          for (const events of results) {
            for (const event of events) {
              const key = makeEventKey(event.title, event.startDate);
              if (!uniqueMap.has(key)) {
                uniqueMap.set(key, event);
              }
            }
          }

          freshEvents = withinDays(Array.from(uniqueMap.values()), ALL_DAYS);
          freshEvents.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
        }

        // Compare with stored events to mark new ones
        if (stored && stored.length > 0) {
          freshEvents = diffEvents(stored, freshEvents);
        }

        // Apply any cached times immediately (fast, no network)
        const timeCache = loadTimeCache();
        for (const event of freshEvents) {
          if (event.link && timeCache[event.link]) {
            event.startDate = new Date(event.startDate);
            event.startDate.setHours(timeCache[event.link].hours, timeCache[event.link].minutes, 0, 0);
          }
        }

        // Save to both caches
        cacheRef.current.set('__all__', { events: freshEvents, fetchedAt: Date.now(), fromNetwork: true });
        saveEventsToStorage(freshEvents);

        return freshEvents;
      } finally {
        pendingRef.current.delete('__all__');
      }
    })();

    pendingRef.current.set('__all__', promise);

    // If we have stored data, return it immediately and let fetch update in background
    if (!fresh && stored && stored.length > 0) {
      // Fire the promise but don't await — it updates cache for next render
      promise.then(freshEvents => {
        cacheRef.current.set('__all__', { events: freshEvents, fetchedAt: Date.now(), fromNetwork: true });
      });
      return stored;
    }

    return promise;
  }, [getApiEvents, fetchAllFromSource]);

  const enrichTimes = useCallback(async (events: Event[]): Promise<Event[]> => {
    const enriched = await enrichEventsWithTimes(events);
    // Update caches after enrichment
    cacheRef.current.set('__all__', { events: enriched, fetchedAt: Date.now() });
    saveEventsToStorage(enriched);
    return enriched;
  }, []);

  const invalidate = useCallback((source?: string) => {
    if (source) {
      cacheRef.current.delete(source);
    } else {
      cacheRef.current.clear();
    }
  }, []);

  return (
    <EventsContext.Provider value={{ getEvents, getAllEvents, enrichTimes, invalidate }}>
      {children}
    </EventsContext.Provider>
  );
};

export const useEvents = () => {
  const ctx = useContext(EventsContext);
  if (!ctx) throw new Error('useEvents must be used within EventsProvider');
  return ctx;
};
